// apps/client/src/pages/reports/BrokerLoanBooksPage.tsx
// BROKER LOAN BOOKS - Cross-template loan book analysis per broker
// RBAC: super_admin, administrator only (enterprise feature)

import React, { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp, RefreshCw, BookOpen, Users, Vault, PiggyBank } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { COMMISSION_GST_BASIS } from '../../utils/commissionDisplay';
import { useBrokerLoanBooks, BrokerRecord, TemplateSummary } from '../../hooks/useBrokerLoanBooks';
import { useLoanPortfolioStats } from '../../hooks/useLoanPortfolioStats';
import { useMonthlyAggregates } from '../../hooks/useMonthlyAggregates';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';
import { getMonthRangeFromPeriod } from '../../utils/periodUtils';
import { formatCurrency, formatCurrencyShorthand } from '../../utils/formatters';
import { resolveLoanBookSelection } from '../../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';

interface OutletContextType {
  userData: any;
  companyData: any;
  userVisibleOffices: any[];
}

type SortField = 'brokerDisplay' | 'totalLoanCount' | 'totalCommissionCents';
type SortDirection = 'asc' | 'desc';

function formatDateRange(first: string, last: string): string {
  if (!first && !last) return 'N/A';
  const fmt = (d: string) => {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  };
  if (first === last) return fmt(first);
  return `${fmt(first)} - ${fmt(last)}`;
}

const BrokerLoanBooksPage: React.FC = () => {
  const { userData, userVisibleOffices, loanBooks = [] } = useOutletContext<OutletContextType & { loanBooks?: ResolvedLoanBook[] }>() as any;
  const navigate = useNavigate();
  const [selectedOffice, setSelectedOffice] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all');
  const [sortField, setSortField] = useState<SortField>('totalCommissionCents');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const hasEntityLoanBooks = useMemo(() =>
    (loanBooks as ResolvedLoanBook[]).some((lb: ResolvedLoanBook) => lb.type === 'entity'),
    [loanBooks]
  );
  const [selectedLoanBook, setSelectedLoanBook] = useState('all');
  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  const { data, loading, error, refetch } = useBrokerLoanBooks({
    companyId: userData?.companyId || '',
    officeId: selectedOffice || undefined,
    status: statusFilter,
    entityCode: resolvedParams.entityCode,
  });

  // Reuse same hooks as Dashboard + Loan Portfolio for consistent stat card numbers
  const { stats: portfolioStats } = useLoanPortfolioStats();
  const { earliestMonth } = useCompanyDataYearRange({ companyId: userData?.companyId });
  const allTimeRange = useMemo(
    () => getMonthRangeFromPeriod('ALL', new Date(), earliestMonth),
    [earliestMonth]
  );
  const { upfrontCommission, trailCommission } = useMonthlyAggregates({
    companyId: userData?.companyId || '',
    startMonth: allTimeRange.startMonth,
    endMonth: allTimeRange.endMonth,
    entityCode: resolvedParams.entityCode,
  });

  const sortedBrokers = useMemo(() => {
    if (!data?.brokers) return [];
    return [...data.brokers].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'brokerDisplay') {
        cmp = a.brokerDisplay.localeCompare(b.brokerDisplay);
      } else {
        cmp = (a[sortField] as number) - (b[sortField] as number);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [data?.brokers, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'brokerDisplay' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-3 h-3 text-blue-400" />
      : <ChevronDown className="w-3 h-3 text-blue-400" />;
  };

  if (!userData?.companyId) {
    return <div className="space-y-6">No company data available.</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BookOpen}
        title="Broker Loan Books"
        subtitle={`Loan counts and commission (${COMMISSION_GST_BASIS}) by broker and office - not loan-book balances`}
        onRefresh={refetch}
        isRefreshing={loading}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Brokers</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
            {userVisibleOffices?.length > 1 && (
              <select
                value={selectedOffice}
                onChange={(e) => setSelectedOffice(e.target.value)}
                className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Offices</option>
                {userVisibleOffices.map((office: any) => (
                  <option key={office.id} value={office.id}>{office.name}</option>
                ))}
              </select>
            )}
            {hasEntityLoanBooks && (
              <select
                value={selectedLoanBook}
                onChange={(e) => setSelectedLoanBook(e.target.value)}
                className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Loan Books</option>
                {(loanBooks as ResolvedLoanBook[]).map((lb: ResolvedLoanBook) => (
                  <option key={lb.id} value={lb.id}>{lb.name}</option>
                ))}
              </select>
            )}
          </div>
        }
      />

      {/* Loading state */}
      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
          <span className="ml-3 text-gray-400">Loading broker loan books...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-6">
          <p className="text-red-300">Failed to load data: {error}</p>
          <button onClick={refetch} className="mt-2 text-sm text-red-400 hover:text-red-300 underline">
            Retry
          </button>
        </div>
      )}

      {data && (
        <>
          {/* Summary stat cards — Total Loans from Loan Portfolio, Total Commission from Dashboard (ALL) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Total Brokers"
              value={data.totals.brokerCount.toLocaleString()}
              subtitle="For selected filters"
              icon={<Users size={24} />}
              gradientColor="#3b82f6"
            />
            <StatCard
              title="Total Loans"
              value={(portfolioStats?.totalLoansEver ?? 0).toLocaleString()}
              subtitle="All loans recorded"
              icon={<Vault size={24} />}
              gradientColor="#8b5cf6"
            />
            <StatCard
              title="Total Commission"
              value={formatCurrencyShorthand(upfrontCommission + trailCommission)}
              subtitle="All time"
              icon={<PiggyBank size={24} />}
              gradientColor="#10b981"
            />
          </div>

          {/* Template Summary Cards */}
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Vault className="w-5 h-5 text-purple-400" />
            Loan Books
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.templateSummaries.map((t: TemplateSummary) => (
              <div
                key={t.templateId}
                className="relative rounded-lg shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300"
              >
                {/* Dark gradient base */}
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.8) 0%, rgba(31, 41, 55, 0.95) 50%, rgba(17, 24, 39, 1) 100%)' }}
                />
                {/* Top accent line */}
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, #3b82f6 15%, #3b82f6 85%, transparent 100%)' }}
                />
                {/* Content */}
                <div className="relative p-5 z-10">
                  <h3 className="font-semibold text-white mb-3 truncate" title={t.templateName}>{t.templateName}</h3>
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div>
                      <span className="text-gray-400">Brokers</span>
                      <p className="text-white font-medium">{t.brokerCount}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Loans</span>
                      <p className="text-white font-medium">{t.loanCount.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Commission</span>
                      <p className="text-white font-medium">{formatCurrencyShorthand(t.totalCommissionCents)}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Date Range</span>
                      <p className="text-white font-medium text-xs">{formatDateRange(t.firstRunDate, t.lastRunDate)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Broker Detail Table */}
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Broker Breakdown
            <span className="text-sm text-gray-400 font-normal ml-2">({sortedBrokers.length} brokers)</span>
          </h2>
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-900/50 border-b border-gray-700">
                  <th
                    className="text-left px-4 py-3 text-sm text-gray-400 font-medium cursor-pointer hover:text-white select-none"
                    onClick={() => handleSort('brokerDisplay')}
                  >
                    <span className="flex items-center gap-1">Broker <SortIcon field="brokerDisplay" /></span>
                  </th>
                  <th className="text-center px-4 py-3 text-sm text-gray-400 font-medium">Status</th>
                  <th className="text-center px-4 py-3 text-sm text-gray-400 font-medium">Books</th>
                  <th
                    className="text-right px-4 py-3 text-sm text-gray-400 font-medium cursor-pointer hover:text-white select-none"
                    onClick={() => handleSort('totalLoanCount')}
                  >
                    <span className="flex items-center gap-1 justify-end">Loans <SortIcon field="totalLoanCount" /></span>
                  </th>
                  <th className="text-right px-4 py-3 text-sm text-gray-400 font-medium">Upfront</th>
                  <th className="text-right px-4 py-3 text-sm text-gray-400 font-medium">Trail</th>
                  <th
                    className="text-right px-4 py-3 text-sm text-gray-400 font-medium cursor-pointer hover:text-white select-none"
                    onClick={() => handleSort('totalCommissionCents')}
                  >
                    <span className="flex items-center gap-1 justify-end">Total <SortIcon field="totalCommissionCents" /></span>
                  </th>
                  <th className="w-8 px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {sortedBrokers.map((broker: BrokerRecord) => (
                  <tr
                    key={broker.brokerDisplay}
                    className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer transition-colors"
                    onClick={() => navigate(`/reports/broker-report/${encodeURIComponent(broker.brokerDisplay)}`)}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-white">{broker.brokerDisplay}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        broker.brokerStatus === 'active' ? 'bg-green-900/50 text-green-300' :
                        broker.brokerStatus === 'inactive' ? 'bg-gray-700 text-gray-400' :
                        broker.brokerStatus === 'pending' ? 'bg-yellow-900/50 text-yellow-300' :
                        'bg-gray-700 text-gray-500'
                      }`}>
                        {broker.brokerStatus === 'unknown' ? '—' : broker.brokerStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-300">{broker.templates.length}</td>
                    <td className="px-4 py-3 text-sm text-right text-white">{broker.totalLoanCount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-300">{formatCurrency(broker.totalUpfrontCents)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-300">{formatCurrency(broker.totalTrailCents)}</td>
                    <td className="px-4 py-3 text-sm text-right text-white font-medium">{formatCurrency(broker.totalCommissionCents)}</td>
                    <td className="px-2 py-3 text-center">
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {sortedBrokers.length === 0 && !loading && (
              <div className="py-12 text-center text-gray-500">
                No broker data found{selectedOffice ? ' for this office' : ''}.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BrokerLoanBooksPage;
